import React, { useState, useEffect } from 'react';

interface TalentKeyUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  super_admin_role: 'SUPER_ADMIN' | 'SUPPORT' | 'MARKETING' | 'SALES' | 'DEVELOPER';
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  last_login?: string;
}

interface ViewUserModalProps {
  user: TalentKeyUser | null;
  isOpen: boolean;
  onClose: () => void;
}

interface EditRoleModalProps {
  user: TalentKeyUser | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (userId: string, newRole: string) => void;
}

interface AddTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: any) => void;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  confirmColor: 'red' | 'green';
  onConfirm: () => void;
  onCancel: () => void;
}

const getErrorMessageFromResponse = async (response: Response, fallbackMessage: string) => {
  try {
    const errorBody = await response.json();
    const message = errorBody?.message || errorBody?.detail;

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  } catch (error) {
    console.error('Failed to parse error response:', error);
  }

  return fallbackMessage;
};

const ViewUserModal: React.FC<ViewUserModalProps> = ({ user, isOpen, onClose }) => {
  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-[480px] shadow-xl rounded-xl bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">User Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{user.first_name} {user.last_name}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{user.email}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{user.super_admin_role.replace('_', ' ')}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
              {user.is_active ? 'Active' : 'Inactive'} • {user.is_verified ? 'Verified' : 'Unverified'}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{new Date(user.created_at).toLocaleString()}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
              {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>
        
        <div className="mt-8 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const EditRoleModal: React.FC<EditRoleModalProps> = ({ user, isOpen, onClose, onSave }) => {
  const [selectedRole, setSelectedRole] = useState<string>('');
  
  useEffect(() => {
    if (user) {
      setSelectedRole(user.super_admin_role);
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const roles = [
    { value: 'SUPER_ADMIN', label: 'Super Admin', description: 'Full system access and management' },
    { value: 'SUPPORT', label: 'Support', description: 'Customer support and user assistance' },
    { value: 'MARKETING', label: 'Marketing', description: 'Marketing tools and analytics' },
    { value: 'SALES', label: 'Sales', description: 'Sales tools and company management' },
    { value: 'DEVELOPER', label: 'Developer', description: 'Technical support and debugging' }
  ];

  const handleSave = () => {
    if (selectedRole !== user.super_admin_role) {
      onSave(user.id, selectedRole);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-[480px] shadow-xl rounded-xl bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Edit User Role</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="mb-6 bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800 font-medium">
            Changing role for: {user.first_name} {user.last_name}
          </p>
          <p className="text-sm text-blue-600">{user.email}</p>
        </div>
        
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Select Role</label>
          {roles.map((role) => (
            <div key={role.value} className="flex items-start p-3 border rounded-lg hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                id={role.value}
                name="role"
                value={role.value}
                checked={selectedRole === role.value}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="mt-1 mr-3 text-indigo-600"
              />
              <div className="flex-1">
                <label htmlFor={role.value} className="text-sm font-medium text-gray-900 cursor-pointer block">
                  {role.label}
                </label>
                <p className="text-xs text-gray-500 mt-1">{role.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-8 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={selectedRole === user.super_admin_role}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    super_admin_role: 'SUPPORT',
    password: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        super_admin_role: 'SUPPORT',
        password: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const roles = [
    { value: 'SUPER_ADMIN', label: 'Super Admin', description: 'Full system access and management' },
    { value: 'SUPPORT', label: 'Support', description: 'Customer support and user assistance' },
    { value: 'MARKETING', label: 'Marketing', description: 'Marketing tools and analytics' },
    { value: 'SALES', label: 'Sales', description: 'Sales tools and company management' },
    { value: 'DEVELOPER', label: 'Developer', description: 'Technical support and debugging' }
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!formData.email.includes('@talentkeyai.com')) newErrors.email = 'Email must be @talentkeyai.com domain';
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error adding team member:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-[480px] shadow-xl rounded-xl bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Add Team Member</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.first_name ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Enter first name"
              />
              {errors.first_name && <p className="text-xs text-red-600 mt-1">{errors.first_name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.last_name ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Enter last name"
              />
              {errors.last_name && <p className="text-xs text-red-600 mt-1">{errors.last_name}</p>}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="user@talentkeyai.com"
            />
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Enter secure password"
            />
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Role</label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {roles.map((role) => (
                <div key={role.value} className="flex items-start p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    id={`add-${role.value}`}
                    name="add-role"
                    value={role.value}
                    checked={formData.super_admin_role === role.value}
                    onChange={(e) => handleInputChange('super_admin_role', e.target.value)}
                    className="mt-1 mr-3 text-indigo-600"
                  />
                  <div className="flex-1">
                    <label htmlFor={`add-${role.value}`} className="text-sm font-medium text-gray-900 cursor-pointer block">
                      {role.label}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-8 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 flex items-center"
          >
            {isSubmitting && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSubmitting ? 'Adding...' : 'Add Team Member'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
  isOpen, title, message, confirmText, confirmColor, onConfirm, onCancel 
}) => {
  if (!isOpen) return null;

  const buttonColors = {
    red: 'bg-red-600 hover:bg-red-700',
    green: 'bg-green-600 hover:bg-green-700'
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-[400px] shadow-xl rounded-xl bg-white">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-6 py-2 text-white rounded-lg transition-colors font-medium ${buttonColors[confirmColor]}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<TalentKeyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'SUPER_ADMIN' | 'SUPPORT' | 'MARKETING' | 'SALES' | 'DEVELOPER'>('all');
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [addTeamMemberModalOpen, setAddTeamMemberModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TalentKeyUser | null>(null);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    confirmColor: 'red' | 'green';
    action: () => void;
  } | null>(null);

  useEffect(() => {
    loadTalentKeyUsers();
  }, []);

  const loadTalentKeyUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.error('No access token found');
        return;
      }
      
      const response = await fetch('/api/v1/super-admin/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ TalentKey users loaded:', data);
        setUsers(data.users || []);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to load TalentKey users:', response.status, errorText);
        
        if (response.status === 401) {
          console.error('❌ Authentication failed - please log in again as super admin');
        }
        
        setUsers([]);
      }
    } catch (error) {
      console.error('❌ Error loading TalentKey users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user: TalentKeyUser) => {
    setSelectedUser(user);
    setViewModalOpen(true);
  };

  const handleEditRole = (user: TalentKeyUser) => {
    setSelectedUser(user);
    setEditRoleModalOpen(true);
  };

  const handleAddTeamMember = async (userData: any) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch('/api/v1/super-admin/users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        console.log('✅ Team member added successfully');
        // Refresh the users list
        await loadTalentKeyUsers();
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to add team member:', response.status, errorText);
        alert('Failed to add team member. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error adding team member:', error);
      alert('An error occurred while adding the team member.');
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/api/v1/super-admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ super_admin_role: newRole })
      });

      if (response.ok) {
        console.log('✅ User role updated successfully');
        // Refresh the users list
        await loadTalentKeyUsers();
      } else {
        const errorMessage = await getErrorMessageFromResponse(
          response,
          'Failed to update user role. Please try again.'
        );
        console.error('❌ Failed to update user role:', response.status, errorMessage);
        alert(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error updating user role:', error);
      alert('An error occurred while updating the user role.');
    }
  };

  const handleToggleStatus = (user: TalentKeyUser) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const actionText = user.is_active ? 'Deactivate' : 'Activate';
    
    setConfirmationConfig({
      title: `${actionText} User`,
      message: `Are you sure you want to ${action} ${user.first_name} ${user.last_name}? ${
        user.is_active ? 'They will lose access to the system.' : 'They will regain access to the system.'
      }`,
      confirmText: actionText,
      confirmColor: user.is_active ? 'red' : 'green',
      action: () => performStatusToggle(user.id, user.is_active)
    });
    setConfirmationModalOpen(true);
  };

  const performStatusToggle = async (userId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/api/v1/super-admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        console.log('✅ User status updated successfully');
        // Refresh the users list
        await loadTalentKeyUsers();
      } else {
        const errorMessage = await getErrorMessageFromResponse(
          response,
          'Failed to update user status. Please try again.'
        );
        console.error('❌ Failed to update user status:', response.status, errorMessage);
        alert(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error updating user status:', error);
      alert('An error occurred while updating the user status.');
    }
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true;
    return user.super_admin_role === filter;
  });

  const getRoleBadge = (role: string) => {
    const colors = {
      SUPER_ADMIN: 'bg-red-100 text-red-800',
      SUPPORT: 'bg-blue-100 text-blue-800',
      MARKETING: 'bg-purple-100 text-purple-800',
      SALES: 'bg-green-100 text-green-800',
      DEVELOPER: 'bg-gray-100 text-gray-800'
    };

    const labels = {
      SUPER_ADMIN: 'Super Admin',
      SUPPORT: 'Support',
      MARKETING: 'Marketing',
      SALES: 'Sales',
      DEVELOPER: 'Developer'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[role as keyof typeof colors]}`}>
        {labels[role as keyof typeof labels]}
      </span>
    );
  };

  const getStatusBadge = (isActive: boolean, isVerified: boolean) => {
    if (!isVerified) {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Unverified</span>;
    }
    if (!isActive) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Inactive</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Active</span>;
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">TalentKey.AI Team Management</h1>
        <div className="bg-white rounded-lg shadow">
          <div className="animate-pulse p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">TalentKey.AI Team Management</h1>
          <p className="text-gray-600">Manage internal team members and their roles</p>
        </div>
        <button 
          onClick={() => setAddTeamMemberModalOpen(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Add Team Member
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'all', label: 'All Team Members', count: users.length },
              { key: 'SUPER_ADMIN', label: 'Super Admins', count: users.filter(u => u.super_admin_role === 'SUPER_ADMIN').length },
              { key: 'SUPPORT', label: 'Support', count: users.filter(u => u.super_admin_role === 'SUPPORT').length },
              { key: 'MARKETING', label: 'Marketing', count: users.filter(u => u.super_admin_role === 'MARKETING').length },
              { key: 'SALES', label: 'Sales', count: users.filter(u => u.super_admin_role === 'SALES').length },
              { key: 'DEVELOPER', label: 'Developers', count: users.filter(u => u.super_admin_role === 'DEVELOPER').length }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key as any)}
                className={`${
                  filter === tab.key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {filter === 'all' ? 'All Team Members' : filter.replace('_', ' ')} ({filteredUsers.length})
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getRoleBadge(user.super_admin_role)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user.is_active, user.is_verified)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button 
                      className="text-indigo-600 hover:text-indigo-900"
                      onClick={() => handleViewUser(user)}
                    >
                      View
                    </button>
                    <button 
                      className="text-green-600 hover:text-green-900"
                      onClick={() => handleEditRole(user)}
                    >
                      Edit Role
                    </button>
                    <button 
                      className={user.is_active ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                      onClick={() => handleToggleStatus(user)}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">No team members found</p>
            <p className="text-xs text-gray-400 mt-1">Add team members to manage roles and permissions</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <ViewUserModal 
        user={selectedUser}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
      />
      
      <EditRoleModal 
        user={selectedUser}
        isOpen={editRoleModalOpen}
        onClose={() => setEditRoleModalOpen(false)}
        onSave={handleRoleChange}
      />
      
      <AddTeamMemberModal 
        isOpen={addTeamMemberModalOpen}
        onClose={() => setAddTeamMemberModalOpen(false)}
        onSave={handleAddTeamMember}
      />
      
      <ConfirmationModal 
        isOpen={confirmationModalOpen}
        title={confirmationConfig?.title || ''}
        message={confirmationConfig?.message || ''}
        confirmText={confirmationConfig?.confirmText || ''}
        confirmColor={confirmationConfig?.confirmColor || 'red'}
        onConfirm={() => {
          confirmationConfig?.action();
          setConfirmationModalOpen(false);
        }}
        onCancel={() => setConfirmationModalOpen(false)}
      />
    </div>
  );
};

export default UserManagement; 
